import { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { FACE_MAP } from '../utils';
import { useAssets } from '../../../context/AssetsContext';

export function getBoxMaterials(
	labelPosition: number,
	baseMaterial: THREE.MeshStandardMaterial,
	labelTextures: Record<number, THREE.Texture>,
	emissiveMap: THREE.Texture | null,
	map = true
): THREE.MeshStandardMaterial[] {
	const materials = Array.from({ length: 6 }, () => baseMaterial.clone());

	let faceIndex: number | null = labelPosition;
	if (map) faceIndex = FACE_MAP[labelPosition];

	if (
		faceIndex !== null &&
		faceIndex >= 0 &&
		faceIndex < 6 &&
		labelTextures[faceIndex] &&
		emissiveMap
	) {
		const labelMaterial = new THREE.MeshStandardMaterial({
			map: labelTextures[faceIndex],
			emissiveMap,
			emissive: new THREE.Color(0xffffff),
			emissiveIntensity: 0.4,
		});
		materials[faceIndex] = labelMaterial;
	}

	return materials;
}

interface UseInstancedBoxesProps {
	scene: THREE.Object3D | null;
	count: number;
	labelPosition: number;
	boxMatrices: THREE.Matrix4[];
}

export function useInstancedBoxes({
	scene,
	count,
	labelPosition,
	boxMatrices,
}: UseInstancedBoxesProps) {
	const instancedBoxesRef = useRef<THREE.InstancedMesh | null>(null);
	const assets = useAssets();

	// Step 1: Memoize geometry and materials once
	const geometry = useMemo(() => new THREE.BoxGeometry(1, 1, 1), []);
	const materials = useMemo(() => {
		const mats =
			assets.cardboardMaterial && assets.labelTextures && assets.emissiveMap
				? getBoxMaterials(
						labelPosition,
						assets.cardboardMaterial,
						assets.labelTextures,
						assets.emissiveMap
				  )
				: [];
		return mats;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		assets.cardboardMaterial,
		assets.labelTextures,
		assets.emissiveMap,
		labelPosition,
	]);

	// Step 2: Create the instanced mesh ONCE when scene and assets are ready
	useEffect(() => {
		if (!scene || !geometry || !materials.length) return;

		const mesh = new THREE.InstancedMesh(geometry, materials, count);
		mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);

		scene.add(mesh);
		instancedBoxesRef.current = mesh;

		return () => {
			scene.remove(mesh);
			mesh.geometry.dispose();
			if (Array.isArray(mesh.material)) {
				mesh.material.forEach(m => m.dispose());
			} else {
				(mesh.material as THREE.Material).dispose();
			}
		};
		// Only run once when scene and required assets are ready
	}, [scene, geometry, materials, count]);

	// Step 3: Only update matrices when data changes
	useEffect(() => {
		const mesh = instancedBoxesRef.current;
		if (!mesh || boxMatrices.length === 0) return;

		mesh.count = boxMatrices.length;
		boxMatrices.forEach((matrix, i) => mesh.setMatrixAt(i, matrix));
		mesh.instanceMatrix.needsUpdate = true;
	}, [boxMatrices]);

	return {
		instancedBoxesRef,
	};
}
